import React from "react";
import { Link } from "react-router-dom";
import { Chip, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import type { BillingAccountSubscriptionDto } from "client/api-v2";
import { DataTable } from "components/DataTable";
import { EmptyValue } from "components/MutedPlaceholder";
import { Overflow, OverflowMenu } from "components/OverflowMenu/OverflowMenu";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { subscriptionStatusColor } from "areas/subscriptions/constants/subscription-status-color";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";

export interface BillingAccountSubscriptionsListProps {
  subscriptions: BillingAccountSubscriptionDto[];
  isReseller: boolean;
  onTransfer: (subscription: BillingAccountSubscriptionDto) => void;
  onRemove: (subscription: BillingAccountSubscriptionDto) => void;
}

function NoSubscriptionsEmptyState() {
  return (
    <Stack spacing={1} sx={{ alignItems: "center", py: 6 }}>
      <Typography variant="subtitle1">No subscriptions on this account yet.</Typography>
      <Typography variant="body2" color="text.secondary">
        Subscriptions appear here once an order or a transfer links them to this account.
      </Typography>
    </Stack>
  );
}

function ResellerEmptyState() {
  return (
    <Stack spacing={1} sx={{ alignItems: "center", py: 6 }}>
      <Typography variant="subtitle1">Reseller accounts don&apos;t own subscriptions.</Typography>
      <Typography variant="body2" color="text.secondary">
        A reseller is invoiced for the subscriptions its customers hold — look on the customer&apos;s own billing
        account to find them.
      </Typography>
    </Stack>
  );
}

function buildColumns(
  onTransfer: BillingAccountSubscriptionsListProps["onTransfer"],
  onRemove: BillingAccountSubscriptionsListProps["onRemove"]
) {
  const columns: GridColDef<BillingAccountSubscriptionDto>[] = [
    {
      field: "serial",
      headerName: "License Serial",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Link to={subscriptionRoutes.detail(params.row.id).root} title="View subscription details">
          {params.row.serial}
        </Link>
      ),
    },
    {
      field: "dnsPrefix",
      headerName: "DNS",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.row.dnsPrefix ?? <EmptyValue />,
    },
    {
      field: "friendlyName",
      headerName: "Friendly Name",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => params.row.friendlyName ?? <EmptyValue />,
    },
    {
      field: "planName",
      headerName: "Plan",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          color={subscriptionStatusColor(params.row.status)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "termEndDate",
      headerName: "Term End",
      minWidth: 140,
      // An evergreen subscription never ends, which the main subscriptions grid also renders as ∞.
      renderCell: (params) => (params.row.termEndDate ? formatDateOnly(DateTime.fromISO(params.row.termEndDate)) : "∞"),
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      align: "right",
      renderCell: (params) => (
        <OverflowMenu
          // Both gate on Admin.ReadWrite, not BillingAccounts.ReadWrite: that is what
          // SubscriptionBillingAccountController demands of assign and unassign alike, so the menu
          // can't offer an action the API would reject.
          items={[
            Overflow.policyDisabledMenuItem(
              RbacPolicies.Admin.ReadWrite,
              "Transfer",
              () => onTransfer(params.row),
              "Only an administrator can move a subscription to another billing account"
            ),
            Overflow.policyDisabledMenuItem(
              RbacPolicies.Admin.ReadWrite,
              "Remove",
              () => onRemove(params.row),
              "Only an administrator can remove a subscription from a billing account"
            ),
          ]}
        />
      ),
    },
  ];
  return columns;
}

export function BillingAccountSubscriptionsList({
  subscriptions,
  isReseller,
  onTransfer,
  onRemove,
}: BillingAccountSubscriptionsListProps) {
  if (subscriptions.length === 0) {
    return isReseller ? <ResellerEmptyState /> : <NoSubscriptionsEmptyState />;
  }

  return (
    <DataTable
      rows={subscriptions}
      columns={buildColumns(onTransfer, onRemove)}
      pagingMode="client"
      sortingMode="client"
      pageSize={10}
      pageSizeOptions={[10, 20]}
    />
  );
}
